// The rules for correcting a lead and adding one by hand, mirrored from the
// server (core-service capture/service/LeadEdits.java). They keep the form
// honest; the server checks everything again and its message wins.
import type { Capture, LeadCreateBody, LeadUpdateBody } from 'api/services/captures.service';
import { roleIn } from 'utils/roleGrants';
import { formatCapturedValue, labelFor } from 'utils/capturedFields';
import { captureFields } from './captureFields';

/**
 * LeadWriteController's @PreAuthorize list, both spellings. BUSINESS_AGENT is
 * deliberately absent, and so is every read-only provider role.
 */
export const LEAD_EDIT_ROLES = [
  'master_admin', 'platform_admin', 'super_admin', 'provider_owner', 'service_admin', 'provider_admin',
  'business_owner', 'operator_admin', 'business_admin', 'operator_user', 'business_user',
];

export const canEditLeads = (role: string | null | undefined): boolean => roleIn(role, LEAD_EDIT_ROLES);

/** Where the pipeline status lives: an ordinary captured field. */
export const LEAD_STATUS_KEY = 'lead_status';

export const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'LOST', 'JUNK'] as const;
export type LeadStatus = typeof LEAD_STATUSES[number];

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: 'New', CONTACTED: 'Contacted', QUALIFIED: 'Qualified', CONVERTED: 'Converted', LOST: 'Lost', JUNK: 'Junk',
};

/** Keys that identify the call or the person. The server refuses an edit to any of them. */
export const IDENTITY_KEYS = [
  'phone', 'call_sid', 'callsid', 'call_id', 'callid', 'call_from', 'call_to',
  'callfrom', 'callto', 'caller_number', 'caller_id', 'route', 'did',
];

export const LIMITS = {
  maxKeys: 50,
  maxValue: 2000,
  maxName: 160,
  maxNotes: 4000,
  maxEmail: 254,
  maxSource: 64,
  maxReference: 120,
};

const KEY_RE = /^[A-Za-z0-9_]{1,64}$/;
const PHONE_RE = /^\+?[0-9][0-9 ()-]{5,19}$/;

/** Only LEAD captures are editable; a booking's state belongs to whoever holds the appointment. */
export const isEditableCapture = (c: Pick<Capture, 'captureType'> | null | undefined): boolean =>
  c?.captureType === 'LEAD';

const isIdentityKey = (k: string) => IDENTITY_KEYS.includes(k.toLowerCase());

/** The captured keys a person may change: not the identity keys, and not the status (it has its own control). */
export const editableFieldKeys = (c: Capture): string[] =>
  Object.keys(captureFields(c))
    .filter((k) => KEY_RE.test(k) && !isIdentityKey(k) && k !== LEAD_STATUS_KEY)
    .sort((a, b) => a.localeCompare(b));

/** The status the lead holds now, upper-cased, or '' when it has none. */
export const currentStatus = (c: Capture): string => {
  const v = captureFields(c)[LEAD_STATUS_KEY];
  return v === null || v === undefined ? '' : String(v).trim().toUpperCase();
};

export interface LeadEditDraft {
  displayName: string;
  notes: string;
  status: string;
  fields: Record<string, string>;
}

export const draftFromCapture = (c: Capture): LeadEditDraft => {
  const raw = captureFields(c);
  const fields: Record<string, string> = {};
  for (const k of editableFieldKeys(c)) fields[k] = formatCapturedValue(raw[k]);
  return { displayName: c.displayName || '', notes: c.notes || '', status: currentStatus(c), fields };
};

/** What is wrong with the edit before it is sent, or null. */
export const validateEdit = (c: Capture, d: LeadEditDraft): string | null => {
  const name = d.displayName.trim();
  const notes = d.notes.trim();
  if (!name && (c.displayName || '').trim()) return 'The name cannot be blanked; correct it instead.';
  if (!notes && (c.notes || '').trim()) return 'Notes cannot be blanked; correct them instead.';
  if (name.length > LIMITS.maxName) return `The name is longer than ${LIMITS.maxName} characters.`;
  if (notes.length > LIMITS.maxNotes) return `Notes are longer than ${LIMITS.maxNotes} characters.`;
  for (const [k, v] of Object.entries(d.fields)) {
    if (v.trim().length > LIMITS.maxValue) return `${labelFor(k)} is longer than ${LIMITS.maxValue} characters.`;
  }
  if (d.status && !(LEAD_STATUSES as readonly string[]).includes(d.status)) return 'Pick a status from the list.';
  return null;
};

/**
 * The PATCH body: only what changed, so an untouched value is absent (unchanged)
 * rather than resent. A field emptied by the user is sent as "" (blanked); a key
 * is never removed.
 */
export const buildUpdateBody = (c: Capture, d: LeadEditDraft): LeadUpdateBody => {
  const body: LeadUpdateBody = {};
  const raw = captureFields(c);
  const fields: Record<string, string> = {};
  for (const k of Object.keys(d.fields)) {
    const next = d.fields[k].trim();
    if (next !== formatCapturedValue(raw[k])) fields[k] = next;
  }
  if (Object.keys(fields).length > 0) body.fields = fields;
  const name = d.displayName.trim();
  if (name && name !== (c.displayName || '').trim()) body.displayName = name;
  const notes = d.notes.trim();
  if (notes && notes !== (c.notes || '').trim()) body.notes = notes;
  if (d.status && d.status !== currentStatus(c)) body.status = d.status;
  return body;
};

export const isEmptyUpdate = (b: LeadUpdateBody): boolean =>
  !b.fields && b.displayName === undefined && b.notes === undefined && b.status === undefined;

export interface ExtraField {
  key: string;
  value: string;
}

export interface LeadCreateDraft {
  phone: string;
  name: string;
  email: string;
  notes: string;
  status: string;
  extra: ExtraField[];
}

export const EMPTY_CREATE_DRAFT: LeadCreateDraft = {
  phone: '', name: '', email: '', notes: '', status: 'NEW', extra: [],
};

export const validateCreate = (d: LeadCreateDraft): string | null => {
  const phone = d.phone.trim();
  if (!phone) return 'A phone number is required.';
  if (!PHONE_RE.test(phone)) return 'Give the phone number in international format, e.g. +919876543210.';
  if (d.name.trim().length > LIMITS.maxName) return `The name is longer than ${LIMITS.maxName} characters.`;
  const email = d.email.trim();
  if (email && !email.includes('@')) return 'That is not an email address.';
  if (email.length > LIMITS.maxEmail) return `The email is longer than ${LIMITS.maxEmail} characters.`;
  if (d.notes.trim().length > LIMITS.maxNotes) return `Notes are longer than ${LIMITS.maxNotes} characters.`;
  const rows = d.extra.filter((r) => r.key.trim() || r.value.trim());
  if (rows.length > LIMITS.maxKeys) return `At most ${LIMITS.maxKeys} fields.`;
  const seen = new Set<string>();
  for (const r of rows) {
    const k = r.key.trim();
    if (!KEY_RE.test(k)) return `Field names must be letters, digits or underscore (got: ${k || 'blank'}).`;
    if (isIdentityKey(k)) return `'${k}' identifies the call or the person and cannot be set as a field.`;
    if (k === LEAD_STATUS_KEY) return 'Use the Status control for the lead status.';
    if (seen.has(k)) return `'${k}' is listed twice.`;
    seen.add(k);
    if (r.value.trim().length > LIMITS.maxValue) return `'${k}' is longer than ${LIMITS.maxValue} characters.`;
  }
  return null;
};

export const buildCreateBody = (d: LeadCreateDraft): LeadCreateBody => {
  const body: LeadCreateBody = { phone: d.phone.trim() };
  if (d.name.trim()) body.name = d.name.trim();
  if (d.email.trim()) body.email = d.email.trim();
  if (d.notes.trim()) body.notes = d.notes.trim();
  if (d.status) body.status = d.status;
  const fields: Record<string, string> = {};
  for (const r of d.extra) {
    if (r.key.trim()) fields[r.key.trim()] = r.value.trim();
  }
  if (Object.keys(fields).length > 0) body.fields = fields;
  return body;
};

/** A change's field, as a person reads it. */
export const changeLabel = (field: string): string => {
  if (field === 'displayName') return 'Name';
  if (field === 'notes') return 'Notes';
  if (field === `fields.${LEAD_STATUS_KEY}`) return 'Status';
  return labelFor(field.replace(/^fields\./, ''));
};

/** A change's value, as a person reads it. Absent and blank are different facts. */
export const changeValue = (v: unknown): string => {
  if (v === null || v === undefined) return '(none)';
  const s = formatCapturedValue(v);
  return s === '' ? '(blank)' : s;
};
